import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { trackEvent, tag } from '../../lib/clarity';
import { rememberPendingRegistration } from '../../lib/pendingRegistration';
import styles from './RegisterForm.module.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// `variant` selects the audience: 'founding' (private invite page, collects a name so the
// welcome email can greet the member) or 'public' (/register, email + password only - the
// competitor-standard short form; name, company and role are collected on /profile).
export default function RegisterForm({ variant = 'founding' }) {
  const isFounding = variant === 'founding';
  const navigate = useNavigate();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const startedRef = useRef(false); // fire the Clarity "started" funnel event once

  // Tags both funnel events so the short form can be compared against the old ten-field
  // form in Clarity without redefining either event.
  function tagFunnel() {
    tag('reg_variant', variant);
    tag('reg_form', 'short');
  }

  function update(field, value) {
    // Funnel: the user has begun filling the form (fires once per session).
    if (!startedRef.current) {
      startedRef.current = true;
      tagFunnel();
      trackEvent('registration_started');
    }
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
    if (serverError) setServerError('');
  }

  function validate() {
    const e = {};
    if (isFounding) {
      if (!form.firstName.trim()) e.firstName = 'First name is required.';
      if (!form.lastName.trim()) e.lastName = 'Last name is required.';
    }

    const email = form.email.trim().toLowerCase();
    if (!email) e.email = 'An email is required.';
    else if (!EMAIL_RE.test(email)) e.email = 'That email doesn’t look right.';

    if (!form.password) e.password = 'Choose a password.';
    else if (form.password.length < 8) e.password = 'Use at least 8 characters.';
    return e;
  }

  async function handleSubmit(evt) {
    evt.preventDefault();
    setServerError('');
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;

    const email = form.email.trim().toLowerCase();
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password: form.password,
          audience: variant,
          ...(isFounding
            ? { firstName: form.firstName.trim(), lastName: form.lastName.trim() }
            : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setServerError(data.error || 'Something went wrong. Please try again.');
        setSubmitting(false);
        return;
      }

      // Funnel: account successfully created. Still fired here rather than after
      // verification, because this event has always meant "the form was completed" and
      // moving it would silently redefine every historical comparison. Verification has
      // its own event on the next page.
      tagFunnel();
      trackEvent('registration_completed');

      // Route on the backend's own flag, never on the absence of an error. A backend that
      // predates verification answers { ok: true } and nothing else - which happens for real
      // in the window where this bundle has deployed and the API has not (the two deploy
      // independently). That backend created the account pre-confirmed and has no
      // verification routes, so sending the user to /verify-email would strand them on a
      // page that can only fail. Signing in is the right next step for that account.
      if (data.verificationRequired !== true) {
        navigate('/login', { replace: true, state: { registered: true, email } });
        return;
      }

      // The account exists but is NOT verified, so there is deliberately no sign-in here -
      // that call is what used to put the user straight into the app. The backend has
      // mailed a 6-digit code; the next step is the page that accepts it.
      //
      // The password is handed over in memory only (see lib/pendingRegistration): the
      // verification request carries it, and the page signs them in with it the moment the
      // address is confirmed, without it ever being written to storage or router history.
      rememberPendingRegistration({ email, password: form.password });

      // Absolute times, computed here from the backend's relative figure, so the page shows
      // the time actually left even after a refresh - react-router keeps this state in
      // window.history, which survives one.
      const sentAt = Date.now();
      const expiresAt = typeof data.expiresInSeconds === 'number'
        ? sentAt + data.expiresInSeconds * 1000
        : null;

      navigate('/verify-email', {
        replace: true,
        state: {
          email,
          // true only when a code actually went out just now. false with `throttled` means
          // the hourly cap held it back and the newest code in the inbox still works; false
          // on its own means the mail failed. The page opens differently for each.
          codeSent: data.codeSent === true,
          throttled: data.throttled === true,
          sentAt: data.codeSent === true ? sentAt : null,
          expiresAt,
        },
      });
    } catch (err) {
      setServerError('Network error - please check your connection and try again.');
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.card}>
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        {isFounding && (
          <div className={styles.grid2}>
            <label className={styles.field}>
              <span className={styles.label}>First name</span>
              <input
                className={`${styles.input} ${errors.firstName ? styles.inputError : ''}`}
                type="text" value={form.firstName}
                onChange={(e) => update('firstName', e.target.value)}
                placeholder="Jordan"
              />
              {errors.firstName && <span className={styles.error}>{errors.firstName}</span>}
            </label>

            <label className={styles.field}>
              <span className={styles.label}>Last name</span>
              <input
                className={`${styles.input} ${errors.lastName ? styles.inputError : ''}`}
                type="text" value={form.lastName}
                onChange={(e) => update('lastName', e.target.value)}
                placeholder="Rivera"
              />
              {errors.lastName && <span className={styles.error}>{errors.lastName}</span>}
            </label>
          </div>
        )}

        <label className={styles.field}>
          <span className={styles.label}>Email</span>
          <input
            className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
            type="email" value={form.email}
            onChange={(e) => update('email', e.target.value)}
            placeholder="you@email.com"
            autoComplete="email"
            autoFocus={!isFounding}
          />
          {errors.email && <span className={styles.error}>{errors.email}</span>}
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Password</span>
          <input
            className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
            type="password" value={form.password}
            onChange={(e) => update('password', e.target.value)}
            placeholder="At least 8 characters"
            autoComplete="new-password"
          />
          {errors.password && <span className={styles.error}>{errors.password}</span>}
        </label>

        {serverError && <div className={styles.serverError}>{serverError}</div>}

        <button className={styles.submit} type="submit" disabled={submitting}>
          {submitting
            ? 'Creating your account…'
            : (isFounding ? 'Claim your Demo Week seat' : 'Create free account')}
        </button>
        {!isFounding && (
          <>
            <p className={styles.formNote}>
              No credit card required · cancel anytime during your trial
            </p>
            {/* Consent line under the button, the way Lusha / Apollo / Hunter do it, so there
                is no checkbox to slow the form down. */}
            <p className={styles.consent}>
              By clicking “Create free account” you agree to our{' '}
              <Link className={styles.consentLink} to="/terms">Terms and Conditions</Link> and{' '}
              <Link className={styles.consentLink} to="/privacy">Privacy Policy</Link>.
            </p>
          </>
        )}
      </form>
    </div>
  );
}
